import { menuListItems } from "assets/menu/menuList";
import { GoToBack } from "components/GoToBack";
import { Header } from "components/Header";
import { Loading } from "components/Loading";
import { Menu } from "components/Menu";
import { PlaceHolderNotExists } from "components/PlaceholderNotExists";
import React from "react";
import { BaseRoutes } from "routes/BaseRoutes";
import {
  STATUS_LABELS,
  formatCents,
  formatCpf,
  formatDateTime,
} from "../formatters";
import { Container, FieldGrid, Section } from "./styles";
import {
  TicketIngestionDetail,
  useTicketIngestionDetails,
} from "./useTicketIngestionDetails";

interface Field {
  label: string;
  value?: string | null;
}

const NOT_SENT = "Não enviado";

const FieldList: React.FC<{ fields: Field[] }> = ({ fields }) => (
  <FieldGrid>
    {fields.map(({ label, value }) => {
      const missing = value === null || value === undefined || value === "";
      return (
        <div key={label}>
          <dt>{label}</dt>
          <dd className={missing ? "missing" : undefined}>
            {missing ? NOT_SENT : value}
          </dd>
        </div>
      );
    })}
  </FieldGrid>
);

const dateOrNull = (date?: string | null) =>
  date ? formatDateTime(date) : null;

const sections = (ticket: TicketIngestionDetail) => [
  {
    title: "Titular",
    fields: [
      { label: "Nome", value: ticket.holderName },
      { label: "CPF", value: ticket.cpf ? formatCpf(ticket.cpf) : null },
      { label: "E-mail", value: ticket.email },
      { label: "Telefone", value: ticket.phone },
      { label: "Empresa", value: ticket.company },
      { label: "Cargo", value: ticket.position },
    ],
  },
  {
    title: "Ingresso",
    fields: [
      { label: "Status", value: STATUS_LABELS[ticket.status] ?? ticket.status },
      { label: "Id do ingresso (parceiro)", value: ticket.externalTicketId },
      { label: "Id do pedido (parceiro)", value: ticket.externalOrderId },
      { label: "Tipo de ingresso", value: ticket.passName },
      { label: "Categoria", value: ticket.category },
      { label: "Evento", value: ticket.eventSlug },
      { label: "Id do evento", value: ticket.eventId },
    ],
  },
  {
    title: "Compra",
    fields: [
      { label: "Origem", value: ticket.source },
      { label: "Data da compra", value: dateOrNull(ticket.purchasedAt) },
      {
        label: "Valor pago",
        value:
          typeof ticket.amountPaidCents === "number"
            ? formatCents(ticket.amountPaidCents)
            : null,
      },
      { label: "Forma de pagamento", value: ticket.paymentMethod },
      {
        label: "Atualizado no parceiro em",
        value: dateOrNull(ticket.sourceUpdatedAt),
      },
    ],
  },
  {
    title: "Controle Confi",
    fields: [
      { label: "Recebido em", value: formatDateTime(ticket.insertDate) },
      { label: "Última alteração", value: formatDateTime(ticket.updateDate) },
      { label: "Último envio", value: dateOrNull(ticket.lastSeenDate) },
      { label: "Último lote", value: ticket.lastBatchId },
      { label: "Processado", value: ticket.processed ? "Sim" : "Não" },
      { label: "Data do processamento", value: dateOrNull(ticket.processedDate) },
      { label: "Id da inscrição", value: ticket.registrationId },
      { label: "Id do cliente", value: ticket.customerId },
      { label: "Erro no processamento", value: ticket.errorMessage },
    ],
  },
];

export const TicketIngestionDetails: React.FC = () => {
  const { loading, error, ticket } = useTicketIngestionDetails();

  return (
    <Menu links={menuListItems}>
      <Header>
        <GoToBack path={BaseRoutes.ticketIngestions} />
      </Header>
      {loading ? (
        <Loading />
      ) : error ? (
        <PlaceHolderNotExists title="Erro ao se comunicar com o servidor" />
      ) : !ticket ? (
        <PlaceHolderNotExists title="Ingresso não encontrado" />
      ) : (
        <Container>
          {sections(ticket).map((section) => (
            <Section key={section.title}>
              <h2 style={{ marginBottom: "1.5rem" }}>{section.title}</h2>
              <FieldList fields={section.fields} />
            </Section>
          ))}
        </Container>
      )}
    </Menu>
  );
};
